/**
 * Centralized Port Configuration (Frontend)
 *
 * SINGLE SOURCE OF TRUTH: /config/ports.json
 *
 * Production: Set VITE_API_URL environment variable to override
 * Example: VITE_API_URL=https://uttapi.ginuga.com
 *
 * Note: Frontend Vite dev server ports (3002/4002) are set via PM2 command line.
 */

import portsConfig from '../../config/ports.json';

// Export ports from central config
export const PORTS = {
  // Environment-specific
  BACKEND: portsConfig.production.backend,
  BACKEND_STAGING: portsConfig.staging.backend,
  FRONTEND: portsConfig.production.frontend,
  FRONTEND_STAGING: portsConfig.staging.frontend,
  // Shared services (same for prod and staging)
  TRADOVATE: portsConfig.shared.tradovate,
  CME_MARKET_HOURS: portsConfig.shared.cmeMarketHours,
  ACCOUNT_STATS: portsConfig.shared.accountStats,
  // Legacy
  BACKEND_LEGACY: portsConfig.legacy.backendNodejs,
};

// Backend type selection - Rust is now the default
// Set VITE_BACKEND_TYPE=nodejs to use legacy Node.js backend
const VITE_BACKEND_TYPE = import.meta.env.VITE_BACKEND_TYPE as string | undefined;
export const USE_LEGACY_BACKEND = VITE_BACKEND_TYPE === 'nodejs';

// Detect if running on staging based on frontend port or hostname
const isStaging = (): boolean => {
  if (typeof window === 'undefined') return false;
  // Check if hostname indicates staging
  if (window.location.hostname.includes('staging')) return true;
  // Check if port is staging frontend port (4002)
  if (window.location.port === String(PORTS.FRONTEND_STAGING)) return true;
  return false;
};

// Select backend port based on environment
const getActiveBackendPort = (): number => {
  if (USE_LEGACY_BACKEND) return PORTS.BACKEND_LEGACY;
  return isStaging() ? PORTS.BACKEND_STAGING : PORTS.BACKEND;
};

const ACTIVE_BACKEND_PORT = getActiveBackendPort();

// Log backend selection on startup (only in browser)
if (typeof window !== 'undefined') {
  const env = isStaging() ? 'staging' : 'production';
  console.log(`[Backend] ${env.toUpperCase()} - Using ${USE_LEGACY_BACKEND ? 'Node.js (legacy)' : 'Rust'} backend on port ${ACTIVE_BACKEND_PORT}`);
}

// Check for production API URL from environment
const VITE_API_URL = import.meta.env.VITE_API_URL as string | undefined;

// Production hostname mapping - when running on these hosts, use the API URL
const PRODUCTION_HOST_MAP: Record<string, string> = {
  'utt.ginuga.com': portsConfig.urls.production.api,
  'staging-utt.ginuga.com': portsConfig.urls.staging.api,
};

export const CONFIG = {
  BACKEND_PORT: PORTS.BACKEND,
  FRONTEND_PORT: PORTS.FRONTEND,

  // Generate backend URL based on current hostname
  // In production, use VITE_API_URL or hostname mapping; in dev, auto-detect
  getBackendUrl: (hostname?: string): string => {
    // If VITE_API_URL is set, use it directly (production build)
    if (VITE_API_URL) {
      return VITE_API_URL;
    }

    const host = hostname || (typeof window !== 'undefined' ? window.location.hostname : 'localhost');

    // Check production hostname mapping (for dev server accessed via proxy)
    if (PRODUCTION_HOST_MAP[host]) {
      return PRODUCTION_HOST_MAP[host];
    }

    // Detect protocol - use HTTPS if current page is HTTPS
    const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
    const protocol = isSecure ? 'https' : 'http';

    return `${protocol}://${host}:${ACTIVE_BACKEND_PORT}`;
  },

  // API endpoints
  getApiBaseUrl: (hostname?: string): string => {
    return `${CONFIG.getBackendUrl(hostname)}/api`;
  },

  getPaperTradingApiUrl: (hostname?: string): string => {
    return `${CONFIG.getBackendUrl(hostname)}/api/paper-trading`;
  },

  getBacktestApiUrl: (hostname?: string): string => {
    return `${CONFIG.getBackendUrl(hostname)}/api/backtest-sessions`;
  },
};

// IMPORTANT: These are getter functions, NOT constants!
// They must be called at runtime to get the correct URL based on window.location
// Using constants would freeze the URL at build time (wrong for production builds)
export const getBackendUrl = () => VITE_API_URL || CONFIG.getBackendUrl();
export const getApiBaseUrl = () => CONFIG.getApiBaseUrl();
export const getPaperTradingApiUrl = () => CONFIG.getPaperTradingApiUrl();
export const getBacktestApiUrl = () => CONFIG.getBacktestApiUrl();

// Legacy exports for backwards compatibility - DEPRECATED, use functions above
// These will be frozen at import time, which is wrong for production builds
export const BACKEND_URL = VITE_API_URL || CONFIG.getBackendUrl();
export const API_BASE_URL = CONFIG.getApiBaseUrl();
export const PAPER_TRADING_API_URL = CONFIG.getPaperTradingApiUrl();
export const BACKTEST_API_URL = CONFIG.getBacktestApiUrl();

// Broker configuration
export const DEFAULT_BROKER_ID = 'tradeify'
